import { Component, Input, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ModalContent } from 'src/app/core/interface';
import { Company, User, UserGroup } from 'src/app/core/models';
import { AlertItem, AlertType } from 'src/app/core/models/AlertItem';
import {
  AlertService, ModalService, UserGroupService, UserService,
  ContractService, StorageKey, StorageService, ContractGlobalService
} from 'src/app/core/services';
import { CompanyService } from 'src/app/core/services/company.service';
import { BaseModal } from 'src/app/core/utils/BaseModal';
import { validateCpf } from 'src/app/core/utils/validateCpf';
import { Gb } from 'src/app/core/services/growthbook.service';

@Component({
  selector: 'app-user-create',
  templateUrl: './user-create.component.html',
  styleUrls: ['./user-create.component.sass']
})
export class UserCreateComponent extends BaseModal implements OnInit, ModalContent {
  @Input() initialState;
  public userForm: FormGroup;
  public currentUser: User;
  public userGroups: Array<UserGroup>;
  public userGroupContracts = [];
  public companies: Array<Company>;
  public isValid = false;
  public user: User;
  formGroupDisabled: boolean;
  public contractIds = null;
  public promise: Promise<any>;
  public documentFiles = [];
  public gb: Gb;
  public featureFlagGestorAdmin: boolean;

  constructor(
    private formBuilder: FormBuilder,
    private userService: UserService,
    private contractsGlobalService: ContractGlobalService,
    private userGroupService: UserGroupService,
    private companyService: CompanyService,
    private alertService: AlertService,
    private contractService: ContractService,
    private storageService: StorageService,
    modalService: ModalService
  ) {
    super(modalService);
    this.gb = new Gb();
  }

  async ngOnInit() {
    this.createForm();
    const currentPermissions = this.storageService.get(StorageKey.currentPermissions);

    this.currentUser = this.storageService.get(StorageKey.currentUser);

    this.companyService.getAll().then(list => {
      if (this.currentUser.superUser) {
        this.companies = list;
      } else {
        this.companies = list.filter(a => a.id === this.currentUser.companyId);
      }
    });
    if (this.currentUser && this.currentUser.superUser === false) {
      this.contractIds = currentPermissions &&
        currentPermissions.filter(contractPermission =>
          contractPermission.actionIds.indexOf('CreateUser') >= 0)
          .map(contract => contract.contractId);
    }
    this.loadUserGroup(this.contractIds);

    if (this.initialState != null) {
      const keys = Object.keys(this.initialState);
      for (const key of keys) {
        const form = this.userForm.get(key);
        if (form) {
          form.setValue(this.initialState[key]);
        }
      }
    }
  }

  loadFiles() {
    const files = [];
    this.documentFiles.map(file => {
      files.push(file.data);
    });
    return files;
  }

  get lastState() {
    const state = {};
    const keys = Object.keys(this.userForm.controls);
    for (const key of keys) {
      state[key] = this.userForm.get(key).value;
    }
    return state;
  }

  async createForm() {
    this.userForm = this.formBuilder.group({
      email: ['', [Validators.required, Validators.email]],
      companyId: ['', [Validators.required]],
      avatar: [''],
      cpf: [''],
      superUser: [false],
      adminManagerUser: [false],
      adminUser: [false],
      role: [''],
      registryNumber: ['', [Validators.maxLength(10)]],
      groupIds: [''],
      extraData: [''],
      files: ['']
    });
    await this.gb.init();
    this.featureFlagGestorAdmin = this.gb.isOn('EM-231');
    this.userForm.valueChanges.subscribe((x) => {
      if (!x.companyId) {
        this.formGroupDisabled = true;
      } else {
        this.formGroupDisabled = false;
      }

      this.isValid = this.userForm.valid;
    });

    this.userForm.get('adminManagerUser')?.valueChanges.subscribe((isGestor: boolean) => {
      if (isGestor) {
        this.userForm.get('adminUser')?.setValue(true, { emitEvent: false });
      }
    });

    this.userForm.get('files').valueChanges.subscribe(data => {
      this.loadFiles();
    });

    // await this.gb.init();
    if (this.gb.isOn('ecs-3952')) {
      const cpf = this.userForm.get('cpf');
      this.userForm.get('cpf').valueChanges.subscribe(data => {
        const cpfIsValid = validateCpf(data);
        if (data && !cpfIsValid) {
          cpf.setErrors({ cpf: 'cpf invalid' });
        }
        if (data === '') {
          cpf.clearValidators();
        }
      });
    }
  }

  userGroupsByCompany(company) {
    this.userGroupContracts = [];
    let contractIds = null;
    this.companyService.getById(company)
      .then(data => {
        if (this.currentUser.superUser) {
          contractIds = data.contractIds;
        } else {
          contractIds = data.contractIds.filter(contractId => this.contractIds.indexOf(contractId) >= 0);
        }
        this.loadUserGroup(contractIds);
      }).catch(err => {
        console.log(err);
      });
  }

  public async onAllSubmited() {
    if (this.promise != null) {
      return this.promise;
    }
    const cpf = this.userForm.get('cpf').value;
    if (cpf && !validateCpf(cpf)) {
      return this.alertService.show(new AlertItem('InvalidCpf', AlertType.danger));
    }
    // this.userForm.get('companyId').valueChanges.subscribe(companyId => {
    //   const groupIdsControl = this.userForm.get('groupIds');

    //   if (companyId) {
    //     if (this.userGroupContracts.length > 0) {
    //       groupIdsControl.setValidators([Validators.required]);
    //     } else {
    //       groupIdsControl.clearValidators();
    //     }
    //     groupIdsControl.updateValueAndValidity();
    //   }
    // });
    const files = (this.userForm.get('files').value || []).map(f => f.isNew === true ? f.detail : f.data);
    this.promise = this.userService.create(User.create({
      email: this.userForm.get('email').value,
      cpf: cpf.replace(/\D+/g, ''),
      registryNumber: this.userForm.get('registryNumber').value,
      companyId: this.userForm.get('companyId').value,
      role: this.userForm.get('role').value,
      avatar: this.userForm.get('avatar').value,
      superUser: this.userForm.get('superUser').value,
      adminManagerUser: this.userForm.get('adminManagerUser').value || false,
      adminUser: this.userForm.get('adminUser').value || false,
      groupIds: this.userForm.get('groupIds').value,
      verificationUrl: window.location.origin + '/verification',
      extraData: {
        observation: this.userForm.get('extraData').value
      },
      files
    }), true).then(res => {
      this.alertService.show(new AlertItem('UserSaved', AlertType.success));
      this.user = res;
      return res;
    }).catch(error => {
      if (error.status === 409) {
        this.alertService.show(new AlertItem('UserSaveConflict', AlertType.danger));
      } else {
        this.alertService.show(new AlertItem('UserSaveError', AlertType.danger));
      }
      throw error;
    }).finally(() => {
      this.promise = null;
    });
    return this.promise;
  }

  loadUserGroup(contractIds) {
    let paramsUserGroup = {};
    if (contractIds != null) {
      paramsUserGroup = { 'contractId[contains,in]': `[${contractIds}]` };
    }
    this.userGroupService.getAll(paramsUserGroup).then(userGroups => {
      if (this.contractsGlobalService.contracts?.length > 0) {
        userGroups = userGroups.filter(
          ug => this.contractsGlobalService.contracts.some(ct => ct === ug.contractId)
        );
      }
      this.userGroups = userGroups;
      this.userGroups.forEach(userGroup => {
        this.contractService.getById(userGroup.contractId).then(contract => {
          this.userGroupContracts.push({
            id: userGroup.id,
            prettyName: userGroup.name + ` (${contract.code} - ${contract.name})`
          });
        });
      });
    });
  }
}
